import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Wrench, Send, Loader2, CheckCircle2, Clock, ArrowLeft } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_META = {
  open:        { label: "Open",        cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  in_progress: { label: "In progress", cls: "text-blue-300  bg-blue-500/10  border-blue-500/30" },
  resolved:    { label: "Resolved",    cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
};

function PriorityIcon({ p }) {
  if (p === "urgent") return <AlertTriangle className="w-3.5 h-3.5 text-red-400" strokeWidth={2.25} />;
  return <Wrench className="w-3.5 h-3.5 text-white/40" strokeWidth={2} />;
}

export default function AdminTickets() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active | all | resolved | urgent
  const [active, setActive] = useState(null);     // currently-viewed ticket
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter === "resolved") params.status = "resolved";
      if (filter === "urgent") params.priority = "urgent";
      const { data } = await api.get("/admin/tickets", { params });
      let items = data.items || [];
      if (filter === "active") items = items.filter((t) => t.status !== "resolved");
      setData({ items, counts: data.counts || {} });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [load]);

  const openTicket = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/tickets/${id}`);
      setActive(data);
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tickets/${active.id}/reply`, { body: reply.trim() });
      setActive(data);
      setReply("");
      toast.success("Reply sent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reply");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/tickets/${active.id}`, { status });
      setActive(data);
      toast.success(`Marked ${STATUS_META[status]?.label || status}`);
      load();
    } catch (e) {
      toast.error("Could not update status");
    } finally {
      setBusy(false);
    }
  };

  // -------------------------- Detail view --------------------------
  if (active) {
    return (
      <div data-testid="admin-ticket-detail" className="max-w-3xl">
        <button
          onClick={() => setActive(null)}
          className="flex items-center gap-2 text-xs text-white/50 hover:text-white mb-6"
          data-testid="admin-ticket-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to inbox
        </button>

        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-1">
              <PriorityIcon p={active.priority} />
              <span>{active.priority === "urgent" ? "Urgent" : "Normal"}</span>
              <span className="text-white/20">·</span>
              <span>{active.client_name}</span>
            </div>
            <h1 className="text-2xl font-medium tracking-tight text-white">{active.subject}</h1>
            <div className="text-xs text-white/40 mt-1.5 font-mono">
              {active.opener_name} · {active.opener_email} · {active.opener_phone || "no phone"}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium border ${STATUS_META[active.status]?.cls || ""}`}>
              {STATUS_META[active.status]?.label || active.status}
            </span>
            <div className="flex gap-1">
              {active.status !== "in_progress" && (
                <button onClick={() => setStatus("in_progress")} disabled={busy}
                  className="text-[11px] text-blue-300 hover:bg-blue-500/10 px-2 py-1 rounded border border-blue-500/30">In progress</button>
              )}
              {active.status !== "resolved" && (
                <button onClick={() => setStatus("resolved")} disabled={busy}
                  data-testid="admin-ticket-resolve"
                  className="text-[11px] text-emerald-300 hover:bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/30">Resolve</button>
              )}
              {active.status === "resolved" && (
                <button onClick={() => setStatus("open")} disabled={busy}
                  className="text-[11px] text-amber-300 hover:bg-amber-500/10 px-2 py-1 rounded border border-amber-500/30">Reopen</button>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          {(active.thread || []).map((m) => (
            <div key={m.id}
              className={`p-4 rounded-xl border ${m.author_role === "admin" ? "bg-[#FFB000]/5 border-[#FFB000]/20" : "bg-white/[0.02] border-white/10"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-white/80">
                  {m.author_role === "admin" ? "You" : m.author_name}
                </div>
                <div className="text-[10px] font-mono text-white/30">
                  {m.created_at && formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
                </div>
              </div>
              <div className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">{m.body}</div>
            </div>
          ))}
        </div>

        <div className="bg-[#121214] border border-white/10 rounded-xl p-4">
          <textarea
            data-testid="admin-ticket-reply-input"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply to client…"
            rows={4}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white/90 placeholder-white/30 outline-none focus:border-[#FFB000]/40 transition-colors resize-none"
          />
          <div className="flex justify-end mt-3">
            <button
              onClick={sendReply}
              disabled={busy || !reply.trim()}
              data-testid="admin-ticket-reply-send"
              className="bg-[#FFB000] hover:bg-[#FFC133] disabled:opacity-50 text-black font-semibold text-sm px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send reply
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------- Inbox list --------------------------
  return (
    <div data-testid="admin-tickets-page">
      <div className="mb-8">
        <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">Customer Support</div>
        <h1 className="text-3xl font-medium tracking-tight text-white">Tickets</h1>
        <p className="text-sm text-white/50 mt-2 max-w-2xl">
          Every client message lands here. Urgent tickets fire an immediate SMS to {`+1 (225) 907-4403`}.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[
          ["active", `Active (${(data.counts.open || 0) + (data.counts.in_progress || 0)})`],
          ["urgent", `Urgent open (${data.counts.urgent_open || 0})`],
          ["resolved", `Resolved (${data.counts.resolved || 0})`],
          ["all", "All"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`admin-tickets-filter-${k}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              filter === k
                ? "bg-[#FFB000]/10 text-[#FFB000] border-[#FFB000]/30"
                : "text-white/55 hover:text-white border-white/10 hover:bg-white/[0.04]"
            }`}
          >{label}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="bg-[#121214] border border-white/10 rounded-xl py-16 text-center text-white/40 text-sm" data-testid="admin-tickets-empty">
          No tickets in this view.
        </div>
      ) : (
        <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden divide-y divide-white/5">
          {data.items.map((t) => (
            <button
              key={t.id}
              onClick={() => openTicket(t.id)}
              data-testid={`admin-ticket-row-${t.id}`}
              className="w-full text-left px-5 py-3.5 flex items-center gap-3 hover:bg-white/[0.02] transition-colors"
            >
              <PriorityIcon p={t.priority} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white/90 truncate">{t.subject}</div>
                <div className="text-[11px] text-white/40 mt-0.5 font-mono truncate">
                  {t.client_name} · {t.opener_name}
                </div>
              </div>
              <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${STATUS_META[t.status]?.cls}`}>
                {STATUS_META[t.status]?.label}
              </span>
              <span className="text-[11px] font-mono text-white/40 w-24 text-right">
                {t.updated_at && formatDistanceToNow(new Date(t.updated_at), { addSuffix: true })}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
